'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Save, Search, SearchCode } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { painelInputClass } from '@/components/painel/cms/painel-form';
import {
  painelButtonPrimaryClass,
  painelDescriptionClass,
  painelSurfaceClass,
  painelSwitchClass,
} from '@/components/painel/painel-styles';
import { SeoFieldsEditor } from '@/components/painel/seo/SeoFieldsEditor';
import { usePainelSession } from '@/components/painel/rbac/painel-session-context';
import { sessionHasPermission } from '@/lib/painel-rbac';
import {
  emptySeoEntryFields,
  type SeoCatalogItem,
  type SeoEntryFields,
  type SeoGlobalConfig,
  type SeoStatus,
} from '@/lib/seo';
import {
  fetchSeoCatalog,
  fetchSeoEntry,
  fetchSeoGlobal,
  updateSeoGlobal,
  upsertSeoEntry,
} from '@/services/seo.service';
import { cn } from '@/lib/utils';

const FILTERS: Array<{ id: string; label: string }> = [
  { id: 'all', label: 'Todos' },
  { id: 'guide', label: 'Guias' },
  { id: 'category', label: 'Categorias' },
  { id: 'power', label: 'Powers' },
  { id: 'ace', label: 'Aces' },
  { id: 'page', label: 'Páginas' },
];

function statusLabel(status: SeoStatus): string {
  switch (status) {
    case 'configured':
      return 'SEO configurado';
    case 'partial':
      return 'Parcialmente configurado';
    case 'noindex':
      return 'Noindex';
    default:
      return 'Não configurado';
  }
}

function statusClass(status: SeoStatus): string {
  switch (status) {
    case 'configured':
      return 'border-emerald-400/30 bg-emerald-500/15 text-emerald-200';
    case 'partial':
      return 'border-amber-400/30 bg-amber-500/15 text-amber-100';
    case 'noindex':
      return 'border-rose-400/30 bg-rose-500/15 text-rose-200';
    default:
      return 'border-white/10 bg-white/[0.04] text-slate-400';
  }
}

type TabId = 'global' | 'catalog';

export function PainelSeoPage() {
  const { session } = usePainelSession();
  const canEdit = sessionHasPermission(session, 'seo.manage');

  const [tab, setTab] = useState<TabId>('global');
  const [global, setGlobal] = useState<SeoGlobalConfig | null>(null);
  const [globalDirty, setGlobalDirty] = useState(false);
  const [savingGlobal, setSavingGlobal] = useState(false);

  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [catalog, setCatalog] = useState<SeoCatalogItem[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);

  const [selected, setSelected] = useState<SeoCatalogItem | null>(null);
  const [entry, setEntry] = useState<SeoEntryFields | null>(null);
  const [fallbackTitle, setFallbackTitle] = useState('');
  const [fallbackDescription, setFallbackDescription] = useState('');
  const [entryDirty, setEntryDirty] = useState(false);
  const [savingEntry, setSavingEntry] = useState(false);
  const [entryLoading, setEntryLoading] = useState(false);

  useEffect(() => {
    if (!canEdit) return;
    void fetchSeoGlobal()
      .then((data) => setGlobal(data))
      .catch(() => toast.error('Não foi possível carregar o SEO global.'));
  }, [canEdit]);

  const loadCatalog = useCallback(async () => {
    if (!canEdit) return;
    setCatalogLoading(true);
    try {
      const rows = await fetchSeoCatalog(filter, query);
      setCatalog(rows);
    } catch {
      toast.error('Não foi possível carregar o catálogo SEO.');
    } finally {
      setCatalogLoading(false);
    }
  }, [canEdit, filter, query]);

  useEffect(() => {
    if (tab !== 'catalog') return;
    const t = window.setTimeout(() => {
      void loadCatalog();
    }, 200);
    return () => window.clearTimeout(t);
  }, [tab, loadCatalog]);

  const openEntry = async (item: SeoCatalogItem) => {
    setSelected(item);
    setEntryLoading(true);
    setEntryDirty(false);
    try {
      const data = await fetchSeoEntry({
        entityType: item.entityType,
        entityId: item.entityId,
        pageKey: item.pageKey,
      });
      setEntry(data.entry);
      setFallbackTitle(data.entity.fallbackTitle || data.entity.label || '');
      setFallbackDescription(data.entity.fallbackDescription || '');
    } catch {
      toast.error('Não foi possível abrir este SEO.');
      setEntry(
        emptySeoEntryFields(item.entityType, item.entityId, item.pageKey)
      );
    } finally {
      setEntryLoading(false);
    }
  };

  const saveGlobal = async () => {
    if (!global || !canEdit) return;
    setSavingGlobal(true);
    try {
      const next = await updateSeoGlobal(global);
      setGlobal(next);
      setGlobalDirty(false);
      toast.success('SEO global guardado.');
    } catch {
      toast.error('Falha ao guardar SEO global.');
    } finally {
      setSavingGlobal(false);
    }
  };

  const saveEntry = async () => {
    if (!entry || !canEdit || !selected) return;
    setSavingEntry(true);
    try {
      const data = await upsertSeoEntry({
        entityType: entry.entityType,
        entityId: entry.entityId,
        pageKey: entry.pageKey,
        title: entry.title,
        description: entry.description,
        canonicalUrl: entry.canonicalUrl,
        slugOverride: entry.slugOverride,
        ogTitle: entry.ogTitle,
        ogDescription: entry.ogDescription,
        ogImage: entry.ogImage,
        twitterTitle: entry.twitterTitle,
        twitterDescription: entry.twitterDescription,
        twitterImage: entry.twitterImage,
        twitterCard: entry.twitterCard,
        shareImage: entry.shareImage,
        robotsIndex: entry.robotsIndex,
        robotsFollow: entry.robotsFollow,
        schemaJson: entry.schemaJson,
      });
      setEntry(data.entry);
      setEntryDirty(false);
      toast.success('SEO guardado.');
      void loadCatalog();
    } catch {
      toast.error('Falha ao guardar SEO.');
    } finally {
      setSavingEntry(false);
    }
  };

  const patchGlobal = (partial: Partial<SeoGlobalConfig>) => {
    if (!global) return;
    setGlobal({ ...global, ...partial });
    setGlobalDirty(true);
  };

  const previewBase = useMemo(
    () => (global?.defaultCanonicalBase || 'https://ajuda.xat.com').replace(/\/$/, ''),
    [global?.defaultCanonicalBase]
  );

  if (!canEdit) {
    return (
      <div className={cn(painelSurfaceClass, 'p-6')}>
        <p className="font-poppins text-sm text-amber-100/90">
          Sem permissão para gerenciar SEO (`seo.manage`).
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="mb-1 flex items-center gap-2 text-sky-300/80">
          <SearchCode className="h-5 w-5" aria-hidden />
          <span className="font-poppins text-xs font-semibold uppercase tracking-[0.18em]">
            System
          </span>
        </div>
        <h1 className="font-poppins text-2xl font-bold text-white">SEO</h1>
        <p className={cn(painelDescriptionClass, 'mt-1 max-w-2xl')}>
          Centro de controlo de metadados, Open Graph, Twitter/X, robots e sitemap do portal.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {(
          [
            { id: 'global', label: 'SEO global' },
            { id: 'catalog', label: 'Catálogo' },
          ] as const
        ).map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={cn(
              'rounded-full border px-4 py-1.5 font-poppins text-xs font-semibold transition',
              tab === t.id
                ? 'border-sky-400/40 bg-sky-500/20 text-sky-100'
                : 'border-white/10 bg-white/[0.03] text-slate-400 hover:border-white/20 hover:text-slate-200'
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'global' && global ? (
        <div className={cn(painelSurfaceClass, 'space-y-5 p-5 sm:p-6')}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="font-poppins text-sm font-semibold text-white">Defaults do site</p>
            <Button
              type="button"
              disabled={!globalDirty || savingGlobal}
              className={cn(painelButtonPrimaryClass, 'h-9 gap-2')}
              onClick={() => void saveGlobal()}
            >
              {savingGlobal ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Guardar
            </Button>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            {(
              [
                ['siteName', 'Nome do site'],
                ['defaultTitle', 'Meta title padrão'],
                ['defaultCanonicalBase', 'URL canónica base'],
                ['defaultShareImage', 'Imagem padrão de partilha'],
                ['ogTitle', 'OG title padrão'],
                ['ogImage', 'OG image padrão'],
                ['twitterTitle', 'Twitter title padrão'],
                ['twitterImage', 'Twitter image padrão'],
                ['twitterCard', 'Twitter card type'],
              ] as const
            ).map(([key, label]) => (
              <div key={key} className="space-y-2">
                <Label className="font-poppins text-slate-400">{label}</Label>
                <Input
                  value={String(global[key] ?? '')}
                  onChange={(e) => patchGlobal({ [key]: e.target.value })}
                  className={painelInputClass}
                />
              </div>
            ))}
            <div className="space-y-2 sm:col-span-2">
              <Label className="font-poppins text-slate-400">Meta description padrão</Label>
              <Input
                value={global.defaultDescription}
                onChange={(e) => patchGlobal({ defaultDescription: e.target.value })}
                className={painelInputClass}
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label className="font-poppins text-slate-400">OG description padrão</Label>
              <Input
                value={global.ogDescription}
                onChange={(e) => patchGlobal({ ogDescription: e.target.value })}
                className={painelInputClass}
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label className="font-poppins text-slate-400">Twitter description padrão</Label>
              <Input
                value={global.twitterDescription}
                onChange={(e) => patchGlobal({ twitterDescription: e.target.value })}
                className={painelInputClass}
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-6 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
            <label className="flex items-center gap-2">
              <Switch
                checked={global.robotsIndex}
                onCheckedChange={(v) => patchGlobal({ robotsIndex: v })}
                className={painelSwitchClass}
              />
              <span className="font-poppins text-sm text-slate-300">Indexar por padrão</span>
            </label>
            <label className="flex items-center gap-2">
              <Switch
                checked={global.robotsFollow}
                onCheckedChange={(v) => patchGlobal({ robotsFollow: v })}
                className={painelSwitchClass}
              />
              <span className="font-poppins text-sm text-slate-300">Follow por padrão</span>
            </label>
            <label className="flex items-center gap-2">
              <Switch
                checked={global.sitemapEnabled}
                onCheckedChange={(v) => patchGlobal({ sitemapEnabled: v })}
                className={painelSwitchClass}
              />
              <span className="font-poppins text-sm text-slate-300">Sitemap ativo</span>
            </label>
          </div>
        </div>
      ) : null}

      {tab === 'catalog' ? (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
          <div className={cn(painelSurfaceClass, 'flex min-h-[420px] flex-col overflow-hidden')}>
            <div className="space-y-3 border-b border-white/10 p-4">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Pesquisar por nome…"
                  className={cn(painelInputClass, 'pl-9')}
                />
              </div>
              <div className="flex flex-wrap gap-1.5">
                {FILTERS.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => setFilter(f.id)}
                    className={cn(
                      'rounded-full border px-2.5 py-1 font-poppins text-[11px] font-semibold',
                      filter === f.id
                        ? 'border-sky-400/40 bg-sky-500/20 text-sky-100'
                        : 'border-white/10 text-slate-400 hover:border-white/20'
                    )}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-2">
              {catalogLoading ? (
                <div className="flex justify-center py-10">
                  <Loader2 className="h-5 w-5 animate-spin text-sky-300" />
                </div>
              ) : catalog.length === 0 ? (
                <p className="p-4 font-inter text-sm text-slate-500">Nenhum item encontrado.</p>
              ) : (
                <ul className="space-y-1">
                  {catalog.map((item) => {
                    const key = `${item.entityType}-${item.entityId ?? item.pageKey}`;
                    const active =
                      selected?.entityType === item.entityType &&
                      selected?.entityId === item.entityId &&
                      selected?.pageKey === item.pageKey;
                    return (
                      <li key={key}>
                        <button
                          type="button"
                          onClick={() => void openEntry(item)}
                          className={cn(
                            'flex w-full flex-col gap-1 rounded-xl border px-3 py-2.5 text-left transition',
                            active
                              ? 'border-sky-400/40 bg-sky-500/15'
                              : 'border-transparent hover:border-white/10 hover:bg-white/[0.04]'
                          )}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <span className="font-poppins text-sm font-semibold text-white">
                              {item.label}
                            </span>
                            <span
                              className={cn(
                                'shrink-0 rounded-full border px-2 py-0.5 font-poppins text-[10px] font-semibold',
                                statusClass(item.status)
                              )}
                            >
                              {statusLabel(item.status)}
                            </span>
                          </div>
                          <span className="font-mono text-[11px] text-slate-500">{item.path}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>

          <div className={cn(painelSurfaceClass, 'min-h-[420px] p-4 sm:p-5')}>
            {!selected ? (
              <p className="py-16 text-center font-inter text-sm text-slate-500">
                Selecione um item à esquerda para editar o SEO.
              </p>
            ) : entryLoading || !entry ? (
              <div className="flex justify-center py-16">
                <Loader2 className="h-6 w-6 animate-spin text-sky-300" />
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-poppins text-lg font-semibold text-white">{selected.label}</p>
                    <p className="font-mono text-xs text-slate-500">{selected.path}</p>
                  </div>
                  <Button
                    type="button"
                    disabled={!entryDirty || savingEntry}
                    className={cn(painelButtonPrimaryClass, 'h-9 gap-2')}
                    onClick={() => void saveEntry()}
                  >
                    {savingEntry ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                    Guardar SEO
                  </Button>
                </div>
                <SeoFieldsEditor
                  value={entry}
                  onChange={(next) => {
                    setEntry(next);
                    setEntryDirty(true);
                  }}
                  fallbackTitle={fallbackTitle}
                  fallbackDescription={fallbackDescription}
                  previewUrl={`${previewBase}${selected.path}`}
                  siteName={global?.siteName || 'xat Ajuda'}
                />
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
